import { Navigate, useLocation, useParams } from "@/lib/router-compat";
import LocalLandingLayout from "@/components/local/LocalLandingLayout";
import { cityPath, cities, tradeBySlug, tradePath, trades } from "@/lib/localSeo";
import roofingHero from "@/assets/agency/trade-roofing.jpg";
import remodelingHero from "@/assets/agency/trade-remodeling.jpg";
import landscapingHero from "@/assets/agency/trade-landscaping.jpg";

const tradeHeroes: Record<string, { image: string; alt: string }> = {
  roofing: { image: roofingHero, alt: "Roofers installing shingles on a Pacific Northwest home under overcast skies" },
  remodeling: { image: remodelingHero, alt: "Remodeler measuring framing inside a gutted Craftsman house" },
  "general-contracting": { image: remodelingHero, alt: "Contractor measuring framing during an interior remodel" },
  flooring: { image: remodelingHero, alt: "Interior remodel in progress with exposed subfloor and framing" },
  landscaping: { image: landscapingHero, alt: "Landscaper edging a lawn beside a Pacific Northwest home" },
  "pressure-washing": { image: landscapingHero, alt: "Exterior yard work at a Pacific Northwest home on a damp morning" },
  fencing: { image: landscapingHero, alt: "Cedar fence and yard work at a Pacific Northwest home" },
};

const TradeLanding = () => {
  const { city: param } = useParams();
  const { pathname } = useLocation();
  const segment = param ?? pathname.split("/").filter(Boolean).pop() ?? "";
  const trade = tradeBySlug(segment.replace(/-contractors$/, ""));
  if (!trade) return <Navigate to="/contractor-websites" replace />;

  const cityLinks = cities.slice(0, 10).map((c) => ({
    label: `Contractor web design in ${c.name}`,
    to: cityPath(c),
    note: c.angle,
  }));

  const otherCityLinks = cities
    .slice(10)
    .map((c) => ({ label: `Contractor web design in ${c.name}`, to: cityPath(c) }));

  const term = trade.designTerm ?? `${trade.label} Contractor Web Design`;

  return (
    <LocalLandingLayout
      title={`${term} | Seattle & Puget Sound, WA | jaik`}
      description={
        trade.seoDescription ??
        `Web design and local SEO built for Puget Sound ${trade.business}. Service pages, Google Business Profile work and lead capture that fills your schedule. Free mockup.`
      }
      canonicalPath={tradePath(trade)}
      eyebrow={`Puget Sound · ${trade.label}`}
      h1={`${term} for Washington ${trade.business}`}
      lead={`A ${trade.keyword} that ranks for the jobs you want and makes it obvious how to hire you. Designed, maintained and optimized by one person who answers the phone.`}
      bodyHeading={`How ${trade.business} win online around Puget Sound`}
      body={trade.intro}
      extra={
        trade.deepDive && (
          <div className="mt-8 space-y-8">
            {trade.deepDive.map((block) => (
              <div key={block.heading}>
                <h3 className="font-heading text-xl font-semibold tracking-tight mb-3">{block.heading}</h3>
                {block.paragraphs.map((p) => (
                  <p key={p.slice(0, 40)} className="font-sans text-agency-text-muted leading-relaxed mb-3 last:mb-0">
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )
      }
      checklistTitle={`Pages your ${trade.label.toLowerCase()} site needs`}
      checklist={trade.pages}
      linkSections={[
        {
          title: `${trade.label} web design by city`,
          intro: `Every city page targets the searches homeowners in that city actually type. ${trade.buyer}`,
          links: cityLinks,
        },
        { title: "More cities I serve", links: otherCityLinks },
        {
          title: "Web design for other trades",
          links: trades
            .filter((t) => t.slug !== trade.slug)
            .map((t) => ({ label: `${t.label} contractor web design`, to: tradePath(t) })),
        },
      ]}
      faqs={[
        ...trade.faqs,
        {
          q: `Do you understand the ${trade.label.toLowerCase()} trade specifically?`,
          a: `I build for trades only. That means I already know which ${trade.label.toLowerCase()} pages earn search traffic, which ones close jobs, and which ones are a waste of your money.`,
        },
        {
          q: "What is included every month?",
          a: "Hosting, security, backups, site updates, on-page SEO, Google Business Profile updates and review responses. Plans start at $400 a month.",
        },
      ]}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "King County Contractors", path: "/king-county-contractors" },
        { name: `${trade.label} contractors`, path: tradePath(trade) },
      ]}
      serviceName={`${trade.label} contractor web design and SEO`}
      areaServed={cities.map((c) => c.name)}
      heroImage={tradeHeroes[trade.slug]?.image}
      heroAlt={tradeHeroes[trade.slug]?.alt}
    />
  );
};

export default TradeLanding;
